import { useState, FormEvent } from 'react';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7j9tb83a2kq2q6kqsg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="bg-[#1a3a3a] py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden pointer-events-none">
          <div
            className="absolute top-0 right-0 w-full h-full opacity-10"
            style={{ background: 'repeating-linear-gradient(135deg, #c8a84b 0px, #c8a84b 2px, transparent 2px, transparent 24px)' }}
          />
        </div>
        <div className="absolute top-0 left-0 w-1 h-full bg-[#c8a84b]" />
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#c8a84b]" />
            <span className="text-[#c8a84b] text-xs font-bold tracking-[0.3em] uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Get in Touch</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Contact Us
          </h1>
          <p className="text-white/65 text-lg leading-relaxed max-w-2xl" style={{ fontFamily: "'Inter', sans-serif" }}>
            Have a staffing need or want to learn more? Let us know and we&apos;ll get back to you right away.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-[#f8f7f2]">
        <div className="max-w-6xl mx-auto px-8 md:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info panel */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-1 bg-[#c8a84b] self-stretch rounded-full shrink-0" style={{ minHeight: '40px' }} />
                  <h2 className="text-2xl font-extrabold text-[#1a3a3a]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Get in Touch</h2>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Have a staffing need or want to learn more? Let us know and we&apos;ll get back to you right away.
                </p>
              </div>

              {/* Contact details — rack card style */}
              <div className="bg-[#1a3a3a] rounded-lg p-8">
                <h3 className="text-white font-bold text-sm mb-6 pb-4 border-b border-white/15" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Contact Information
                </h3>
                <ul className="space-y-5">
                  {[
                    { icon: 'ri-phone-line', label: 'Phone', value: '(480) 269-7513' },
                    { icon: 'ri-mail-line', label: 'Email', value: 'info@continuous-staffing.com' },
                    { icon: 'ri-global-line', label: 'Website', value: 'www.continuous-staffing.com' },
                    { icon: 'ri-time-line', label: 'Hours', value: 'Available 24/7' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-md bg-[#c8a84b]/20 shrink-0">
                        <i className={`${item.icon} text-[#c8a84b] text-sm`} />
                      </div>
                      <div>
                        <div className="text-white/40 text-xs font-bold tracking-widest uppercase mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item.label}</div>
                        <div className="text-white text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{item.value}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quote block */}
              <div className="bg-white border-l-4 border-[#c8a84b] rounded-r-lg px-6 py-5">
                <p className="text-[#1a3a3a] text-sm italic leading-relaxed" style={{ fontFamily: "'Georgia', serif" }}>
                  &ldquo;Our business is built on relationships and results. Give us a chance to prove ourselves.&rdquo;
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 bg-white rounded-lg border border-gray-100 p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full mb-6" style={{ background: 'rgba(26,58,58,0.08)' }}>
                    <i className="ri-check-line text-3xl text-[#1a3a3a]" />
                  </div>
                  <h3 className="text-2xl font-extrabold text-[#1a3a3a] mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>Message Sent!</h3>
                  <p className="text-gray-500 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                    Thank you for reaching out. A member of our team will be in touch right away.
                  </p>
                </div>
              ) : (
                <form data-readdy-form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
                  <div className="pb-4 border-b border-gray-100">
                    <h3 className="text-lg font-extrabold text-[#1a3a3a]" style={{ fontFamily: "'Montserrat', sans-serif" }}>Send Us a Message</h3>
                    <p className="text-gray-400 text-xs mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>We&apos;ll get back to you right away.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>First Name *</label>
                      <input type="text" name="first_name" required placeholder="Jane" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] placeholder-gray-300 focus:outline-none focus:border-[#c8a84b] transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Last Name *</label>
                      <input type="text" name="last_name" required placeholder="Smith" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] placeholder-gray-300 focus:outline-none focus:border-[#c8a84b] transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Email *</label>
                    <input type="email" name="email" required placeholder="jane@company.com" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] placeholder-gray-300 focus:outline-none focus:border-[#c8a84b] transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Company</label>
                    <input type="text" name="company" placeholder="Acme Corp" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] placeholder-gray-300 focus:outline-none focus:border-[#c8a84b] transition-colors duration-200" style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Service Needed</label>
                    <select name="service" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] focus:outline-none focus:border-[#c8a84b] transition-colors duration-200 cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <option value="">Select a service...</option>
                      <option value="Temporary Staffing">Temporary Staffing</option>
                      <option value="Temp-to-Hire">Temp-to-Hire</option>
                      <option value="Not Sure">Not Sure Yet</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold tracking-widest uppercase text-gray-400 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      Message * <span className="normal-case font-normal text-gray-300">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us about your staffing needs..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-[#1a3a3a] placeholder-gray-300 focus:outline-none focus:border-[#c8a84b] transition-colors duration-200 resize-none"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                    {charCount > 500 && <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || charCount > 500}
                    className="w-full py-4 rounded-md bg-[#c8a84b] text-white font-bold text-sm tracking-wide hover:bg-[#b8943b] transition-colors duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
